<template>
  <component
    :is="componentType"
    ref="inputRef"
    v-bind="attrs"
    v-model:value="innerValue"
    autocomplete="off"
    :allow-clear="allowClear"
    :disabled="disabled"
    :readonly="readonly"
    :maxlength="getMaxlength"
    :class="[
      bem.b(),
      bem.is(isGray ? 'gray-input' : ''),
      allowClear && !!innerValue && isMouseOver && !disabled
        ? 'show-clear-icon'
        : '',
    ]"
    @change="onChange"
  >
    <template v-for="item in Object.keys($slots)" :key="item" #[item]="data">
      <slot :name="item" v-bind="data || {}"></slot>
    </template>
    <template v-if="prefixIcon" #prefix>
      <ll-icon :icon-name="prefixIcon" class="prefix-icon" />
    </template>
    <template v-if="suffixIcon" #suffix>
      <ll-icon :icon-name="suffixIcon" class="suffix-icon" />
    </template>
  </component>
</template>

<script setup lang="ts">
import { createNamespace } from '@ll-plus/utils'
import { Input } from 'ant-design-vue'
import { computed, nextTick, onMounted, onUnmounted, ref, useAttrs } from 'vue'
import { InputValue, inputProps } from './input'

defineOptions({ name: 'LlInput', inheritAttrs: false })

const bem = createNamespace('input')

const props = defineProps(inputProps)

const emit = defineEmits(['update:value', 'change', 'clear'])

const attrs = useAttrs()

const inputRef = ref()
const isMouseOver = ref(false)
const affixWrapper = ref()

const innerValue = computed({
  get() {
    return props.value || props.defaultValue
  },
  set(val: InputValue) {
    emit('update:value', val)
  },
})

const getMaxlength = computed(() =>
  typeof props.maxlength === 'number'
    ? props.maxlength > 0
      ? props.maxlength
      : null
    : null,
)

// 鼠标移入事件
const handleMouseOver = () => {
  isMouseOver.value = true
}

// 鼠标移出事件
const handleMouseOut = () => {
  isMouseOver.value = false
}

// 输入框发送变化的回调
const onChange = (e: any) => {
  const { value } = e.target
  emit('change', e)
  if (!value && e.type === 'click') {
    emit('clear', value)
  }
}

// 根据showPassword属性决定组件类型
const componentType = props.showPassword ? Input.Password : Input

const focus = () => {
  inputRef.value?.focus?.()
}
const select = () => {
  inputRef.value?.select?.()
}

const handleWrapperMouseOver = () => handleMouseOver()
const handleWrapperMouseOut = () => handleMouseOut()

onMounted(() => {
  nextTick(() => {
    if (!inputRef.value || !inputRef.value.$el || props.showPassword) return

    // 简化逻辑：先检查根元素是否已经是目标元素
    const rootElement = inputRef.value.$el
    const isRootAffixWrapper = rootElement.classList?.contains(
      'ant-input-affix-wrapper',
    )

    if (isRootAffixWrapper) {
      // 情况1：根元素本身就是 affix-wrapper
      affixWrapper.value = rootElement
    } else {
      // 情况2：在子元素中查找
      affixWrapper.value = rootElement.querySelector?.(
        '.ant-input-affix-wrapper',
      )
    }
    if (affixWrapper.value) {
      affixWrapper.value.addEventListener('mouseenter', handleWrapperMouseOver)
      affixWrapper.value.addEventListener('mouseleave', handleWrapperMouseOut)
    }
  })
})

onUnmounted(() => {
  if (affixWrapper.value) {
    affixWrapper.value.removeEventListener('mouseenter', handleWrapperMouseOver)
    affixWrapper.value.removeEventListener('mouseleave', handleWrapperMouseOut)
  }
})

// 公开focus方法
defineExpose({
  focus,
  select,
})
</script>
